import * as z from 'zod';

export const QueryMessagesRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z.enum(['_id', '_createdDate']).optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query options.'),
});
export const QueryMessagesResponse = z.object({
  messages: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Message ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        conversationId: z
          .string()
          .describe('Conversation ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        sender: z
          .enum(['USER', 'ASSISTANT', 'GENERATED_BY_AI'])
          .describe('Message sender type.')
          .optional(),
        feedback: z
          .object({
            text: z
              .string()
              .describe('Feedback text.')
              .max(4096)
              .optional()
              .nullable(),
            document: z
              .object({
                _id: z
                  .string()
                  .describe('ID of the external document.')
                  .max(36)
                  .optional(),
                title: z
                  .string()
                  .describe('Title of the external document.')
                  .max(1000)
                  .optional(),
                description: z
                  .string()
                  .describe('Description of the external document.')
                  .max(100000)
                  .optional(),
                tags: z.array(z.string()).max(10).optional(),
                url: z
                  .string()
                  .describe('URL of the external document.')
                  .max(1000)
                  .optional(),
                category: z
                  .enum(['ENRICHMENT', 'RESTRICTION'])
                  .describe('Category of the external document.')
                  .optional(),
                action: z
                  .enum(['NO_ANSWER', 'CONTACT'])
                  .describe(
                    'Action to be taken when the external document is presented to the user.'
                  )
                  .optional(),
                isFeedback: z
                  .boolean()
                  .describe(
                    'Whether the external document is generated from a feedback.'
                  )
                  .optional()
                  .nullable(),
                isPublished: z
                  .boolean()
                  .describe('Whether the external document is published.')
                  .optional()
                  .nullable(),
                updateTs: z
                  .date()
                  .describe(
                    'Timestamp of the last update of the external document.'
                  )
                  .optional()
                  .nullable(),
                relevance: z
                  .number()
                  .describe('Relevance of the external document.')
                  .optional()
                  .nullable(),
                usage: z
                  .number()
                  .int()
                  .describe('Usage of the external document.')
                  .optional()
                  .nullable(),
                reference: z
                  .object({
                    _id: z
                      .string()
                      .describe('ID of the document.')
                      .max(100)
                      .optional(),
                    documentType: z
                      .string()
                      .describe('Type of the document.')
                      .max(50)
                      .optional(),
                    title: z
                      .string()
                      .describe('Title of the document.')
                      .max(1000)
                      .optional(),
                    url: z
                      .string()
                      .describe('URL of the document.')
                      .max(1000)
                      .optional(),
                    img: z
                      .string()
                      .describe('Image of the document.')
                      .max(1000)
                      .optional(),
                  })
                  .describe('Reference to a site document.')
                  .optional(),
              })
              .describe('Document attached to the feedback.')
              .optional(),
            error: z
              .string()
              .describe("Defined if there's an error attaching the document.")
              .max(4096)
              .optional()
              .nullable(),
            type: z
              .enum([
                'THUMBS_UP',
                'IRRELEVANT',
                'INCORRECT',
                'SHOULD_NOT_ANSWER',
                'OK_BUT',
                'OTHER',
              ])
              .describe('Feedback type.')
              .optional(),
          })
          .describe(
            'Feedback attached to the message. Only relevant for messages sent by the AI assistant.'
          )
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time the message was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the message was updated.')
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the message is updated. To prevent conflicting changes, the current revision must be passed when updating the message.\n\nIgnored when creating a message.'
          )
          .optional()
          .nullable(),
        body: z
          .object({
            mainText: z
              .string()
              .describe('Main text of the message.')
              .max(4096)
              .optional()
              .nullable(),
            footerText: z
              .string()
              .describe('Footer text of the message.')
              .max(4096)
              .optional()
              .nullable(),
            additionalData: z
              .array(z.record(z.string(), z.any()))
              .max(30)
              .optional(),
          })
          .describe('Message body.')
          .optional(),
        answerTo: z
          .string()
          .describe('ID of the message that this message is answering.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe('Tags.')
          .optional(),
      })
    )
    .max(1000)
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const AskQuestionRequest = z.object({
  question: z
    .object({
      _id: z
        .string()
        .describe('Message ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      conversationId: z
        .string()
        .describe('Conversation ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      sender: z.enum(['USER', 'ASSISTANT', 'GENERATED_BY_AI']).optional(),
      feedback: z
        .object({
          text: z
            .string()
            .describe('Feedback text.')
            .max(4096)
            .optional()
            .nullable(),
          document: z
            .object({
              _id: z
                .string()
                .describe('ID of the external document.')
                .max(36)
                .optional(),
              title: z
                .string()
                .describe('Title of the external document.')
                .max(1000)
                .optional(),
              description: z
                .string()
                .describe('Description of the external document.')
                .max(100000)
                .optional(),
              tags: z.array(z.string()).max(10).optional(),
              url: z
                .string()
                .describe('URL of the external document.')
                .max(1000)
                .optional(),
              category: z.enum(['ENRICHMENT', 'RESTRICTION']).optional(),
              action: z.enum(['NO_ANSWER', 'CONTACT']).optional(),
              isFeedback: z
                .boolean()
                .describe(
                  'Whether the external document is generated from a feedback.'
                )
                .optional()
                .nullable(),
              isPublished: z
                .boolean()
                .describe('Whether the external document is published.')
                .optional()
                .nullable(),
              updateTs: z
                .date()
                .describe(
                  'Timestamp of the last update of the external document.'
                )
                .optional()
                .nullable(),
              relevance: z
                .number()
                .describe('Relevance of the external document.')
                .optional()
                .nullable(),
              usage: z
                .number()
                .int()
                .describe('Usage of the external document.')
                .optional()
                .nullable(),
              reference: z
                .object({
                  _id: z
                    .string()
                    .describe('ID of the document.')
                    .max(100)
                    .optional(),
                  documentType: z
                    .string()
                    .describe('Type of the document.')
                    .max(50)
                    .optional(),
                  title: z
                    .string()
                    .describe('Title of the document.')
                    .max(1000)
                    .optional(),
                  url: z
                    .string()
                    .describe('URL of the document.')
                    .max(1000)
                    .optional(),
                  img: z
                    .string()
                    .describe('Image of the document.')
                    .max(1000)
                    .optional(),
                })
                .describe('Reference to a site document.')
                .optional(),
            })
            .describe('Document attached to the feedback.')
            .optional(),
          error: z
            .string()
            .describe("Defined if there's an error attaching the document.")
            .max(4096)
            .optional()
            .nullable(),
          type: z
            .enum([
              'THUMBS_UP',
              'IRRELEVANT',
              'INCORRECT',
              'SHOULD_NOT_ANSWER',
              'OK_BUT',
              'OTHER',
            ])
            .optional(),
        })
        .describe(
          'Feedback attached to the message. Only relevant for messages sent by the AI assistant.'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the message was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the message was updated.')
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the message is updated. To prevent conflicting changes, the current revision must be passed when updating the message.\n\nIgnored when creating a message.'
        )
        .optional()
        .nullable(),
      body: z
        .object({
          mainText: z
            .string()
            .describe('Main text of the message.')
            .max(4096)
            .optional()
            .nullable(),
          footerText: z
            .string()
            .describe('Footer text of the message.')
            .max(4096)
            .optional()
            .nullable(),
          additionalData: z
            .array(z.record(z.string(), z.any()))
            .max(30)
            .optional(),
        })
        .describe('Message body.')
        .optional(),
      answerTo: z
        .string()
        .describe('ID of the message that this message is answering.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags.')
        .optional(),
    })
    .describe('Question message.'),
});
export const AskQuestionResponse = z.object({
  answer: z
    .object({
      _id: z
        .string()
        .describe('Message ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      conversationId: z
        .string()
        .describe('Conversation ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      sender: z
        .enum(['USER', 'ASSISTANT', 'GENERATED_BY_AI'])
        .describe('Message sender type.')
        .optional(),
      feedback: z
        .object({
          text: z
            .string()
            .describe('Feedback text.')
            .max(4096)
            .optional()
            .nullable(),
          document: z
            .object({
              _id: z
                .string()
                .describe('ID of the external document.')
                .max(36)
                .optional(),
              title: z
                .string()
                .describe('Title of the external document.')
                .max(1000)
                .optional(),
              description: z
                .string()
                .describe('Description of the external document.')
                .max(100000)
                .optional(),
              tags: z.array(z.string()).max(10).optional(),
              url: z
                .string()
                .describe('URL of the external document.')
                .max(1000)
                .optional(),
              category: z
                .enum(['ENRICHMENT', 'RESTRICTION'])
                .describe('Category of the external document.')
                .optional(),
              action: z
                .enum(['NO_ANSWER', 'CONTACT'])
                .describe(
                  'Action to be taken when the external document is presented to the user.'
                )
                .optional(),
              isFeedback: z
                .boolean()
                .describe(
                  'Whether the external document is generated from a feedback.'
                )
                .optional()
                .nullable(),
              isPublished: z
                .boolean()
                .describe('Whether the external document is published.')
                .optional()
                .nullable(),
              updateTs: z
                .date()
                .describe(
                  'Timestamp of the last update of the external document.'
                )
                .optional()
                .nullable(),
              relevance: z
                .number()
                .describe('Relevance of the external document.')
                .optional()
                .nullable(),
              usage: z
                .number()
                .int()
                .describe('Usage of the external document.')
                .optional()
                .nullable(),
              reference: z
                .object({
                  _id: z
                    .string()
                    .describe('ID of the document.')
                    .max(100)
                    .optional(),
                  documentType: z
                    .string()
                    .describe('Type of the document.')
                    .max(50)
                    .optional(),
                  title: z
                    .string()
                    .describe('Title of the document.')
                    .max(1000)
                    .optional(),
                  url: z
                    .string()
                    .describe('URL of the document.')
                    .max(1000)
                    .optional(),
                  img: z
                    .string()
                    .describe('Image of the document.')
                    .max(1000)
                    .optional(),
                })
                .describe('Reference to a site document.')
                .optional(),
            })
            .describe('Document attached to the feedback.')
            .optional(),
          error: z
            .string()
            .describe("Defined if there's an error attaching the document.")
            .max(4096)
            .optional()
            .nullable(),
          type: z
            .enum([
              'THUMBS_UP',
              'IRRELEVANT',
              'INCORRECT',
              'SHOULD_NOT_ANSWER',
              'OK_BUT',
              'OTHER',
            ])
            .describe('Feedback type.')
            .optional(),
        })
        .describe(
          'Feedback attached to the message. Only relevant for messages sent by the AI assistant.'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the message was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the message was updated.')
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the message is updated. To prevent conflicting changes, the current revision must be passed when updating the message.\n\nIgnored when creating a message.'
        )
        .optional()
        .nullable(),
      body: z
        .object({
          mainText: z
            .string()
            .describe('Main text of the message.')
            .max(4096)
            .optional()
            .nullable(),
          footerText: z
            .string()
            .describe('Footer text of the message.')
            .max(4096)
            .optional()
            .nullable(),
          additionalData: z
            .array(z.record(z.string(), z.any()))
            .max(30)
            .optional(),
        })
        .describe('Message body.')
        .optional(),
      answerTo: z
        .string()
        .describe('ID of the message that this message is answering.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags.')
        .optional(),
    })
    .describe('Answer message.')
    .optional(),
});
export const AddFeedbackRequest = z.object({
  messageId: z
    .string()
    .describe('ID of the message to add feedback to.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    feedback: z
      .object({
        text: z
          .string()
          .describe('Feedback text.')
          .max(4096)
          .optional()
          .nullable(),
        document: z
          .object({
            _id: z
              .string()
              .describe('ID of the external document.')
              .max(36)
              .optional(),
            title: z
              .string()
              .describe('Title of the external document.')
              .max(1000)
              .optional(),
            description: z
              .string()
              .describe('Description of the external document.')
              .max(100000)
              .optional(),
            tags: z.array(z.string()).max(10).optional(),
            url: z
              .string()
              .describe('URL of the external document.')
              .max(1000)
              .optional(),
            category: z.enum(['ENRICHMENT', 'RESTRICTION']).optional(),
            action: z.enum(['NO_ANSWER', 'CONTACT']).optional(),
            isFeedback: z
              .boolean()
              .describe(
                'Whether the external document is generated from a feedback.'
              )
              .optional()
              .nullable(),
            isPublished: z
              .boolean()
              .describe('Whether the external document is published.')
              .optional()
              .nullable(),
            updateTs: z
              .date()
              .describe(
                'Timestamp of the last update of the external document.'
              )
              .optional()
              .nullable(),
            relevance: z
              .number()
              .describe('Relevance of the external document.')
              .optional()
              .nullable(),
            usage: z
              .number()
              .int()
              .describe('Usage of the external document.')
              .optional()
              .nullable(),
            reference: z
              .object({
                _id: z
                  .string()
                  .describe('ID of the document.')
                  .max(100)
                  .optional(),
                documentType: z
                  .string()
                  .describe('Type of the document.')
                  .max(50)
                  .optional(),
                title: z
                  .string()
                  .describe('Title of the document.')
                  .max(1000)
                  .optional(),
                url: z
                  .string()
                  .describe('URL of the document.')
                  .max(1000)
                  .optional(),
                img: z
                  .string()
                  .describe('Image of the document.')
                  .max(1000)
                  .optional(),
              })
              .describe('Reference to a site document.')
              .optional(),
          })
          .describe('Document attached to the feedback.')
          .optional(),
        error: z
          .string()
          .describe("Defined if there's an error attaching the document.")
          .max(4096)
          .optional()
          .nullable(),
        type: z
          .enum([
            'THUMBS_UP',
            'IRRELEVANT',
            'INCORRECT',
            'SHOULD_NOT_ANSWER',
            'OK_BUT',
            'OTHER',
          ])
          .optional(),
      })
      .describe('Feedback to add to the message.'),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe('Latest message revision.'),
  }),
});
export const AddFeedbackResponse = z.object({
  message: z
    .object({
      _id: z
        .string()
        .describe('Message ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      conversationId: z
        .string()
        .describe('Conversation ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      sender: z
        .enum(['USER', 'ASSISTANT', 'GENERATED_BY_AI'])
        .describe('Message sender type.')
        .optional(),
      feedback: z
        .object({
          text: z
            .string()
            .describe('Feedback text.')
            .max(4096)
            .optional()
            .nullable(),
          document: z
            .object({
              _id: z
                .string()
                .describe('ID of the external document.')
                .max(36)
                .optional(),
              title: z
                .string()
                .describe('Title of the external document.')
                .max(1000)
                .optional(),
              description: z
                .string()
                .describe('Description of the external document.')
                .max(100000)
                .optional(),
              tags: z.array(z.string()).max(10).optional(),
              url: z
                .string()
                .describe('URL of the external document.')
                .max(1000)
                .optional(),
              category: z
                .enum(['ENRICHMENT', 'RESTRICTION'])
                .describe('Category of the external document.')
                .optional(),
              action: z
                .enum(['NO_ANSWER', 'CONTACT'])
                .describe(
                  'Action to be taken when the external document is presented to the user.'
                )
                .optional(),
              isFeedback: z
                .boolean()
                .describe(
                  'Whether the external document is generated from a feedback.'
                )
                .optional()
                .nullable(),
              isPublished: z
                .boolean()
                .describe('Whether the external document is published.')
                .optional()
                .nullable(),
              updateTs: z
                .date()
                .describe(
                  'Timestamp of the last update of the external document.'
                )
                .optional()
                .nullable(),
              relevance: z
                .number()
                .describe('Relevance of the external document.')
                .optional()
                .nullable(),
              usage: z
                .number()
                .int()
                .describe('Usage of the external document.')
                .optional()
                .nullable(),
              reference: z
                .object({
                  _id: z
                    .string()
                    .describe('ID of the document.')
                    .max(100)
                    .optional(),
                  documentType: z
                    .string()
                    .describe('Type of the document.')
                    .max(50)
                    .optional(),
                  title: z
                    .string()
                    .describe('Title of the document.')
                    .max(1000)
                    .optional(),
                  url: z
                    .string()
                    .describe('URL of the document.')
                    .max(1000)
                    .optional(),
                  img: z
                    .string()
                    .describe('Image of the document.')
                    .max(1000)
                    .optional(),
                })
                .describe('Reference to a site document.')
                .optional(),
            })
            .describe('Document attached to the feedback.')
            .optional(),
          error: z
            .string()
            .describe("Defined if there's an error attaching the document.")
            .max(4096)
            .optional()
            .nullable(),
          type: z
            .enum([
              'THUMBS_UP',
              'IRRELEVANT',
              'INCORRECT',
              'SHOULD_NOT_ANSWER',
              'OK_BUT',
              'OTHER',
            ])
            .describe('Feedback type.')
            .optional(),
        })
        .describe(
          'Feedback attached to the message. Only relevant for messages sent by the AI assistant.'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the message was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the message was updated.')
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the message is updated. To prevent conflicting changes, the current revision must be passed when updating the message.\n\nIgnored when creating a message.'
        )
        .optional()
        .nullable(),
      body: z
        .object({
          mainText: z
            .string()
            .describe('Main text of the message.')
            .max(4096)
            .optional()
            .nullable(),
          footerText: z
            .string()
            .describe('Footer text of the message.')
            .max(4096)
            .optional()
            .nullable(),
          additionalData: z
            .array(z.record(z.string(), z.any()))
            .max(30)
            .optional(),
        })
        .describe('Message body.')
        .optional(),
      answerTo: z
        .string()
        .describe('ID of the message that this message is answering.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags.')
        .optional(),
    })
    .describe('Updated message.')
    .optional(),
});
export const BulkUpdateMessageTagsRequest = z.object({
  ids: z.array(z.string()).min(1).max(100),
  options: z.object({
    assignTags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
          )
          .optional(),
        tags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe('Tags to assign.'),
    unassignTags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
          )
          .optional(),
        tags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe('Tags to unassign.')
      .optional(),
  }),
});
export const BulkUpdateMessageTagsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Individual message metadata.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata.')
    .optional(),
});
export const BulkUpdateMessageTagsByFilterRequest = z.object({
  filter: z
    .record(z.string(), z.any())
    .describe(
      'Filter to apply to the messages. Tags are updated for messages included in this filter.'
    ),
  options: z.object({
    assignTags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
          )
          .optional(),
        tags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe('Tags to assign.'),
    unassignTags: z
      .object({
        privateTags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
          )
          .optional(),
        tags: z
          .object({ tagIds: z.array(z.string()).max(100).optional() })
          .describe(
            'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
          )
          .optional(),
      })
      .describe('Tags to unassign.')
      .optional(),
  }),
});
export const BulkUpdateMessageTagsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe('Job ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const UpdateExtendedFieldsRequest = z.object({
  _id: z.string().describe('ID of the entity to update.'),
  namespace: z
    .string()
    .describe(
      'Identifier for the app whose extended fields are being updated.'
    ),
  options: z.object({
    namespaceData: z
      .record(z.string(), z.any())
      .describe(
        'Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured.'
      ),
  }),
});
export const UpdateExtendedFieldsResponse = z.object({
  message: z
    .object({
      _id: z
        .string()
        .describe('Message ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      conversationId: z
        .string()
        .describe('Conversation ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      sender: z
        .enum(['USER', 'ASSISTANT', 'GENERATED_BY_AI'])
        .describe('Message sender type.')
        .optional(),
      feedback: z
        .object({
          text: z
            .string()
            .describe('Feedback text.')
            .max(4096)
            .optional()
            .nullable(),
          document: z
            .object({
              _id: z
                .string()
                .describe('ID of the external document.')
                .max(36)
                .optional(),
              title: z
                .string()
                .describe('Title of the external document.')
                .max(1000)
                .optional(),
              description: z
                .string()
                .describe('Description of the external document.')
                .max(100000)
                .optional(),
              tags: z.array(z.string()).max(10).optional(),
              url: z
                .string()
                .describe('URL of the external document.')
                .max(1000)
                .optional(),
              category: z
                .enum(['ENRICHMENT', 'RESTRICTION'])
                .describe('Category of the external document.')
                .optional(),
              action: z
                .enum(['NO_ANSWER', 'CONTACT'])
                .describe(
                  'Action to be taken when the external document is presented to the user.'
                )
                .optional(),
              isFeedback: z
                .boolean()
                .describe(
                  'Whether the external document is generated from a feedback.'
                )
                .optional()
                .nullable(),
              isPublished: z
                .boolean()
                .describe('Whether the external document is published.')
                .optional()
                .nullable(),
              updateTs: z
                .date()
                .describe(
                  'Timestamp of the last update of the external document.'
                )
                .optional()
                .nullable(),
              relevance: z
                .number()
                .describe('Relevance of the external document.')
                .optional()
                .nullable(),
              usage: z
                .number()
                .int()
                .describe('Usage of the external document.')
                .optional()
                .nullable(),
              reference: z
                .object({
                  _id: z
                    .string()
                    .describe('ID of the document.')
                    .max(100)
                    .optional(),
                  documentType: z
                    .string()
                    .describe('Type of the document.')
                    .max(50)
                    .optional(),
                  title: z
                    .string()
                    .describe('Title of the document.')
                    .max(1000)
                    .optional(),
                  url: z
                    .string()
                    .describe('URL of the document.')
                    .max(1000)
                    .optional(),
                  img: z
                    .string()
                    .describe('Image of the document.')
                    .max(1000)
                    .optional(),
                })
                .describe('Reference to a site document.')
                .optional(),
            })
            .describe('Document attached to the feedback.')
            .optional(),
          error: z
            .string()
            .describe("Defined if there's an error attaching the document.")
            .max(4096)
            .optional()
            .nullable(),
          type: z
            .enum([
              'THUMBS_UP',
              'IRRELEVANT',
              'INCORRECT',
              'SHOULD_NOT_ANSWER',
              'OK_BUT',
              'OTHER',
            ])
            .describe('Feedback type.')
            .optional(),
        })
        .describe(
          'Feedback attached to the message. Only relevant for messages sent by the AI assistant.'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the message was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the message was updated.')
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the message is updated. To prevent conflicting changes, the current revision must be passed when updating the message.\n\nIgnored when creating a message.'
        )
        .optional()
        .nullable(),
      body: z
        .object({
          mainText: z
            .string()
            .describe('Main text of the message.')
            .max(4096)
            .optional()
            .nullable(),
          footerText: z
            .string()
            .describe('Footer text of the message.')
            .max(4096)
            .optional()
            .nullable(),
          additionalData: z
            .array(z.record(z.string(), z.any()))
            .max(30)
            .optional(),
        })
        .describe('Message body.')
        .optional(),
      answerTo: z
        .string()
        .describe('ID of the message that this message is answering.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags.')
        .optional(),
    })
    .describe('Updated message.')
    .optional(),
});
